import { Box, Paper, Stack, Typography } from '@mui/material';
import { useContext } from 'react';

import { UserContext } from '~/auth/userContext';
import CreditStatusChip from '~/components/invoices/CreditStatusChip';
import { LiftAITextLink } from '~/components/utils/helpers';
import { type Invoice, invoiceStatusToLabelMap } from '~/data/schemas/invoice';
import { formatCurrency, toDateFormatMMddyyyy } from '~/utils/format';
import { buildUrlForInvoice } from '~/utils/urlBuilder';

const amount = (value: string | number | null | undefined) =>
  value == null || value === '' ? null : Number(value);

/** What a credit reduces and whether it counts, where an invoice shows its decision. */
export function CreditAppliesToCard({ credit }: { credit: Invoice }) {
  const { currencyCode } = useContext(UserContext);
  const target = credit.creditedInvoice;

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }} data-testid="credit-applies-to">
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="overline" color="text.secondary" display="block">
            Credit
          </Typography>
          <Typography variant="h5">
            {/* A credit kept on file doesn't deduct anything, so it isn't shown as one. */}
            {credit.keepOnFileOnly ? '' : '−'}
            {formatCurrency({ value: amount(credit.proposedAmount), currencyCode })}
          </Typography>
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary" display="block">
            Applies to
          </Typography>
          {target ? (
            <>
              <LiftAITextLink
                variant="subtitle1"
                label={`Invoice #${target.number}`}
                href={buildUrlForInvoice(target.id)}
              />
              <Typography variant="body2" color="text.secondary">
                {formatCurrency({ value: amount(target.proposedAmount), currencyCode })} ·{' '}
                {invoiceStatusToLabelMap.get(target.status)}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="subtitle1">Whole account</Typography>
              <Typography variant="body2" color="text.secondary">
                {credit.property?.name ?? 'Not tied to one invoice'}
              </Typography>
            </>
          )}
        </Box>
        <Box>
          <CreditStatusChip credit={credit} />
        </Box>
        {credit.date && credit.countsTowardSpend ? (
          <Typography variant="caption" color="text.secondary">
            Counts on {toDateFormatMMddyyyy(credit.date)}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}
